/*
 * A página de categorias: a barra de navegação no
 * topo, as categorias em caixas (ou em tabela, conforme o admin) e, abaixo,
 * as etiquetas com a contagem de tópicos.
 *
 * Com os endereços próprios das listas ligados, /categories é essa página e
 * /tags vira a página de etiquetas ("Etiquetas", "Ordenar por
 * contagem/nome" e a lista). Sem eles, /tags faz as duas vezes.
 *
 * O TagsPage é do flarum/tags; ele é buscado no registro de módulos só se a
 * extensão estiver ativa, para o tema não depender dela.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Link from 'flarum/common/components/Link';
import classList from 'flarum/common/utils/classList';
import { t } from '../utils/settings';
import { categoryBadge, categorySymbol, isTagsEnabled, separateTagsPage, sortTags, tagSymbol, tagUrl } from '../utils/tags';
import { shortAge, fullDate } from '../utils/dates';
import ListNavigation from './ListNavigation';
function layout() {
  return app.forum.attribute('canelaCategoriesLayout') === 'list' ? 'list' : 'boxes';
}
function childrenOf(tag) {
  const children = tag.children();
  return Array.isArray(children) ? sortTags(children.filter(Boolean)) : [];
}
function colorStyle(tag) {
  const color = tag.color();
  return color ? {
    '--category-color': color
  } : undefined;
}
function boxesView(categories) {
  return m("ul", {
    className: "CanelaCategoryBoxes"
  }, categories.map(tag => {
    const children = childrenOf(tag);
    return m("li", {
      className: "CanelaCategoryBox",
      style: colorStyle(tag)
    }, m("h3", {
      className: "CanelaCategoryBox-title"
    }, m(Link, {
      className: "CanelaCategoryBox-link",
      href: tagUrl(tag)
    }, categorySymbol(tag), m("span", {
      className: "CanelaCategoryBox-name"
    }, tag.name()))), tag.description() && m("p", {
      className: "CanelaCategoryBox-description"
    }, tag.description()), children.length > 0 && m("ul", {
      className: "CanelaCategoryBox-children"
    }, children.map(child => m("li", null, categoryBadge(child)))));
  }));
}
const skeletonLine = width => m("span", {
  className: "CanelaSkeleton CanelaSkeleton--line",
  style: {
    width
  }
});

/** As caixas carregando: nome, descrição de duas linhas e a fileira das filhas. */
function boxesSkeleton() {
  return m("ul", {
    className: "CanelaCategoryBoxes CanelaCategoryBoxes--loading",
    "aria-busy": "true"
  }, ['48%', '36%', '56%', '42%', '30%', '52%'].map(width => m("li", {
    className: "CanelaCategoryBox"
  }, m("h3", {
    className: "CanelaCategoryBox-title"
  }, skeletonLine(width)), m("p", {
    className: "CanelaCategoryBox-description"
  }, skeletonLine('92%'), skeletonLine('60%')), m("ul", {
    className: "CanelaCategoryBox-children"
  }, ['4.5em', '6em', '5em'].map(child => m("li", null, skeletonLine(child)))))));
}

/** A tabela carregando: o cabeçalho de verdade e linhas com barras. */
function listSkeleton() {
  return m("table", {
    className: "CanelaCategoryTable CanelaCategoryTable--loading",
    "aria-busy": "true"
  }, m("thead", null, m("tr", null, m("th", {
    className: "CanelaCategoryTable-category"
  }, t('categories.category')), m("th", {
    className: "CanelaCategoryTable-topics"
  }, t('categories.topics')), m("th", {
    className: "CanelaCategoryTable-latest"
  }, t('categories.latest')))), m("tbody", null, ['30%', '22%', '36%', '26%', '18%'].map(width => m("tr", null, m("td", {
    className: "CanelaCategoryTable-category"
  }, m("h3", {
    className: "CanelaCategoryTable-name"
  }, skeletonLine(width)), m("p", {
    className: "CanelaCategoryTable-description"
  }, skeletonLine('70%'))), m("td", {
    className: "CanelaCategoryTable-topics"
  }, m("span", {
    className: "CanelaSkeleton CanelaSkeleton--word",
    style: {
      width: '1.5em'
    }
  })), m("td", {
    className: "CanelaCategoryTable-latest"
  }, m("div", {
    className: "CanelaCategoryTable-latestInner"
  }, skeletonLine('80%')))))));
}
function latestView(tag) {
  const discussion = tag.lastPostedDiscussion();
  if (!discussion) return m("span", {
    className: "CanelaCategoryTable-none"
  }, "-");
  const date = discussion.lastPostedAt();
  return m("div", {
    className: "CanelaCategoryTable-latestInner"
  }, m(Link, {
    className: "CanelaCategoryTable-latestTitle",
    href: app.route.discussion(discussion, discussion.lastPostNumber() ?? undefined)
  }, discussion.title()), date && m("span", {
    className: "CanelaCategoryTable-latestAge",
    title: fullDate(date)
  }, shortAge(date)));
}
function listView(categories) {
  return m("table", {
    className: "CanelaCategoryTable"
  }, m("thead", null, m("tr", null, m("th", {
    className: "CanelaCategoryTable-category"
  }, t('categories.category')), m("th", {
    className: "CanelaCategoryTable-topics"
  }, t('categories.topics')), m("th", {
    className: "CanelaCategoryTable-latest"
  }, t('categories.latest')))), m("tbody", null, categories.map(tag => {
    const children = childrenOf(tag);
    return m("tr", {
      style: colorStyle(tag)
    }, m("td", {
      className: "CanelaCategoryTable-category"
    }, m("h3", {
      className: "CanelaCategoryTable-name"
    }, m(Link, {
      href: tagUrl(tag)
    }, categorySymbol(tag), m("span", null, tag.name()))), tag.description() && m("p", {
      className: "CanelaCategoryTable-description"
    }, tag.description()), children.length > 0 && m("div", {
      className: "CanelaCategoryTable-children"
    }, children.map(child => categoryBadge(child)))), m("td", {
      className: "CanelaCategoryTable-topics"
    }, tag.discussionCount()), m("td", {
      className: "CanelaCategoryTable-latest"
    }, latestView(tag)));
  })));
}
function tagsView(labels, title = t('categories.tags_title')) {
  return m("section", {
    className: "CanelaTagCloud"
  }, m("h2", {
    className: "CanelaTagCloud-title"
  }, title), m("ul", {
    className: "CanelaTagCloud-list"
  }, labels.map(tag => m("li", {
    className: "CanelaTagCloud-item"
  }, m(Link, {
    className: "CanelaTagCloud-tag",
    href: tagUrl(tag),
    title: tag.description() || undefined,
    style: tag.color() ? {
      '--tag-color': tag.color()
    } : undefined
  }, tagSymbol(tag), m("span", null, tag.name())), m("span", {
    className: "CanelaTagCloud-count"
  }, "x ", tag.discussionCount())))));
}

/** A ordenação da página de etiquetas; dura a sessão. */
let tagSort = 'count';
function sortLink(key, label) {
  return m("a", {
    href: "#",
    className: classList('CanelaTagsPage-sort', {
      active: tagSort === key
    }),
    "aria-pressed": tagSort === key ? 'true' : 'false',
    onclick: event => {
      event.preventDefault();
      tagSort = key;
    }
  }, label);
}

/** A página /tags. */
function tagsPageView(labels) {
  const sorted = tagSort === 'name' ? labels.slice().sort((a, b) => a.name().localeCompare(b.name(), undefined, {
    sensitivity: 'base'
  })) : labels;
  return m("div", {
    className: "CanelaTagsPage"
  }, m("h2", {
    className: "CanelaTagsPage-title"
  }, t('categories.tags_title')), m("div", {
    className: "CanelaTagsPage-sortOptions"
  }, t('categories.tags_sort_by'), " ", sortLink('count', t('categories.tags_sort_count')), " ", sortLink('name', t('categories.tags_sort_name'))), m("hr", {
    className: "CanelaTagsPage-rule"
  }), sorted.length ? tagsView(sorted, t('categories.tags_all')) : m("p", {
    className: "CanelaTagsPage-empty"
  }, t('categories.tags_none')));
}
function onTagsPage() {
  return separateTagsPage() && app.current.get('routeName') === 'tags';
}
export default function installCategoriesPage() {
  if (!isTagsEnabled()) return;
  const TagsPage = flarum.reg.checkModule('flarum-tags', 'forum/components/TagsPage');
  if (!TagsPage) return;

  // O WelcomeHero repetido em cima das categorias só empurraria a grade para
  // baixo; a aba "Categorias" ativa na barra já diz onde o leitor está.
  override(TagsPage.prototype, 'hero', function () {
    return null;
  });
  extend(TagsPage.prototype, 'contentItems', function (items) {
    const tagsPage = onTagsPage();

    // A página de etiquetas não tem a barra de navegação.
    if (!tagsPage) items.add('canelaNav', m(ListNavigation, null), 200);
    if (this.loading) {
      // No lugar do spinner, as caixas (ou a tabela) das categorias com as
      // classes das reais: elas chegam onde o esqueleto estava.
      if (!tagsPage) {
        items.remove('loading');
        items.add('canelaCategories', layout() === 'list' ? listSkeleton() : boxesSkeleton(), 100);
      }
      return;
    }
    const tags = this.tags || [];
    const categories = sortTags(tags.filter(tag => tag.position() !== null && !tag.isChild()));
    const labels = tags.filter(tag => tag.position() === null).sort((a, b) => b.discussionCount() - a.discussionCount());
    items.remove('tagTiles');
    items.remove('cloud');
    if (tagsPage) {
      items.add('canelaTagsPage', tagsPageView(labels), 100);
      return;
    }
    if (categories.length) {
      items.add('canelaCategories', layout() === 'list' ? listView(categories) : boxesView(categories), 100);
    }
    if (labels.length) {
      items.add('canelaTags', tagsView(labels), 10);
    }
  });
}
flarum.reg.add('ramon-canela', 'forum/list/categoriesPage', installCategoriesPage);